import cachedRequest from 'configs/cachedRequest';
import Settings from 'configs/Settings';
import setSurvicateData from 'configs/Survicate';
import Cookie from 'js-cookie';
import { action } from 'mobx';
import GtagService from 'modules/app/services/ga/GtagService';
import { AuthService } from 'modules/auth';
import { DEFAULT_PROFILE_IMAGE_SIZE } from 'modules/file/constants';
import MembershipService from 'modules/membership/MembershipService';
import {
  TranslationService,
  DEFAULT_LOCALE,
  LOCALES
} from 'modules/translation';

import { REF_AGENT_ID, REF_LANG, REF_USER_CONFIGS } from './constants';
import './UserApi';
import UserStore from './UserStore';

import '../content/ContentApi';

import { setCustomConfig } from '@hotmart/club-files-ui';
import Request from '@hotmart/request';

class UserService {
  constructor() {
    this.changeLocale(this.language);
  }

  get isLogged() {
    return true;
  }

  get language() {
    return (
      (UserStore && UserStore.locale) ||
      Cookie.get(REF_LANG) ||
      navigator.language ||
      navigator.userLanguage ||
      DEFAULT_LOCALE
    );
  }

  get hash() {
    return UserStore && UserStore.hash;
  }

  @action
  updateLocale(locale, keys) {
    UserStore.localeKeys = keys;
    UserStore.locale = locale;
  }

  changeLocale = (newLocale) => {
    return new Promise(async (resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const shortLocale = newLocale.slice(0, 2).toUpperCase();
      const locale = LOCALES[shortLocale] || DEFAULT_LOCALE;

      Cookie.set(REF_LANG, locale, Settings.cookieConfig);
      UserStore.updateUserCookie({ locale: newLocale });

      try {
        const keys = await TranslationService.getKeys(locale);

        this.updateLocale(locale, keys);

        resolve(locale);
      } catch (e) {
        reject(e);
      }
    });
  };

  async syncUserData(locale) {
    await AuthService.validateToken();
    await this.fetchAccountData(locale);

    this.hasInvalidToken = false;

    return void 0;
  }

  getAgentId() {
    const value = Cookie.get(REF_AGENT_ID);

    if (value) {
      const agentId = JSON.parse(value);

      sessionStorage.setItem(REF_AGENT_ID, agentId);
      Cookie.remove(REF_AGENT_ID, { domain: Settings.cookieDomain });
    }
    const storageAgentId = sessionStorage.getItem(REF_AGENT_ID);

    return storageAgentId ? JSON.parse(storageAgentId) : null;
  }

  @action
  async fetchAccountData(locale) {
    try {
      const user = await cachedRequest(
        () => Request('accounts'),
        [
          'accounts',
          AuthService.authCheckToken.user_name,
          MembershipService.subdomain
        ]
      );

      if (!user) {
        return;
      }
      const agentId = this.getAgentId();

      UserStore.user = { ...user, agentId };
      setSurvicateData(user);

      setCustomConfig({ name: 'userId', value: user.hash });
      this.setProfileImage(user.pictureFileKey);

      await this.changeLocale(locale || user.locale);

      const GACustomEventData = {
        customEventName: 'club_admin_user_signin',
        customAttributes: {
          userId: user.marketplaceId
        }
      };

      GtagService.sendCustomEvent(GACustomEventData);
    } catch (e) {
      // do nothing
    }
  }

  fetchProfileImage = async (params) => {
    try {
      const response = await Request('avatar', params);
      const { avatarResize } = response;

      return avatarResize;
    } catch {
      // do nothing
    }
  };

  async setProfileImage(fileKey, profileImgUpdate = false) {
    UserStore.user = {
      pictureUrl: null,
      pictureThumbUrl:
        (await this.fetchProfileImage(DEFAULT_PROFILE_IMAGE_SIZE)) || null,
      profileImgUpdate
    };
  }

  @action
  async fetchUserAreaCode() {
    try {
      AuthService.areaCode = await cachedRequest(
        () => Request('get.userAreaCode'),
        [
          'userAreaCode',
          AuthService.authCheckToken.user_name,
          MembershipService.subdomain
        ]
      );
    } catch (e) {
      // do nothing
    }
  }

  setAccountConfigs(configs) {
    if (!configs) {
      return null;
    }

    Cookie.set(REF_USER_CONFIGS, configs, Settings.cookieConfig);
    UserStore.configs = configs;
  }

  decrementLimit() {
    UserStore.limit--;
  }

  initLimit(value) {
    UserStore.limit = value;
  }

  get hasInvalidToken() {
    return UserStore.hasInvalidToken;
  }

  set hasInvalidToken(value) {
    UserStore.hasInvalidToken = value || false;
  }
}

export default new UserService();
